import { useMemo } from 'react';

import type { MessageDescriptor } from 'react-intl-next';

import { token } from '@atlaskit/tokens';

import { extractToken } from '../color-conversion';
import { createBuiltInColorMap } from '../color-map';
import { useDarkModeColors } from '../use-dark-mode-colors';

/** This is a map of commonly used ADS colors, a well-balanced spread of the color palette
 * There are 3 unique sets of tokens for background, text, and border.
 * Highlight colors, otherwise known as foreground colors, are the same as text colors
 * because text colors are handpicked to complement background colors at the appropriate AA contrast ratio
 * contrast standards should be met between foreground/background as text/background does
 */
const ELEVATION_SURFACE_TOKEN = token('elevation.surface');
const ELEVATION_SURFACE_OVERLAY_TOKEN = token('elevation.surface.overlay');
const COLOR_BACKGROUND_ACCENT_BLUE_SUBTLEST_TOKEN = token('color.background.accent.blue.subtlest');
const COLOR_BACKGROUND_ACCENT_PURPLE_SUBTLEST_TOKEN = token(
	'color.background.accent.purple.subtlest',
);
const COLOR_BACKGROUND_ACCENT_MAGENTA_SUBTLEST_TOKEN = token(
	'color.background.accent.magenta.subtlest',
);
const COLOR_BACKGROUND_ACCENT_RED_SUBTLEST_TOKEN = token('color.background.accent.red.subtlest');
const COLOR_BACKGROUND_ACCENT_ORANGE_SUBTLEST_TOKEN = token(
	'color.background.accent.orange.subtlest',
);
const COLOR_BACKGROUND_ACCENT_YELLOW_SUBTLEST_TOKEN = token(
	'color.background.accent.yellow.subtlest',
);
const COLOR_BACKGROUND_ACCENT_LIME_SUBTLEST_TOKEN = token('color.background.accent.lime.subtlest');
const COLOR_BACKGROUND_ACCENT_GREEN_SUBTLEST_TOKEN = token(
	'color.background.accent.green.subtlest',
);
const COLOR_BACKGROUND_ACCENT_TEAL_SUBTLEST_TOKEN = token('color.background.accent.teal.subtlest');
const COLOR_BACKGROUND_ACCENT_GRAY_SUBTLER_TOKEN = token('color.background.accent.gray.subtler');
const COLOR_BACKGROUND_ACCENT_BLUE_SUBTLER_TOKEN = token('color.background.accent.blue.subtler');
const COLOR_BACKGROUND_ACCENT_PURPLE_SUBTLER_TOKEN = token(
	'color.background.accent.purple.subtler',
);
const COLOR_BACKGROUND_ACCENT_MAGENTA_SUBTLER_TOKEN = token(
	'color.background.accent.magenta.subtler',
);
const COLOR_BACKGROUND_ACCENT_RED_SUBTLER_TOKEN = token('color.background.accent.red.subtler');
const COLOR_BACKGROUND_ACCENT_ORANGE_SUBTLER_TOKEN = token(
	'color.background.accent.orange.subtler',
);
const COLOR_BACKGROUND_ACCENT_YELLOW_SUBTLER_TOKEN = token(
	'color.background.accent.yellow.subtler',
);
const COLOR_BACKGROUND_ACCENT_LIME_SUBTLER_TOKEN = token('color.background.accent.lime.subtler');
const COLOR_BACKGROUND_ACCENT_GREEN_SUBTLER_TOKEN = token('color.background.accent.green.subtler');
const COLOR_BACKGROUND_ACCENT_TEAL_SUBTLER_TOKEN = token('color.background.accent.teal.subtler');
const COLOR_BACKGROUND_ACCENT_GRAY_SUBTLE_TOKEN = token('color.background.accent.gray.subtle');
const COLOR_BACKGROUND_ACCENT_BLUE_SUBTLE_TOKEN = token('color.background.accent.blue.subtle');
const COLOR_BACKGROUND_ACCENT_PURPLE_SUBTLE_TOKEN = token('color.background.accent.purple.subtle');
const COLOR_BACKGROUND_ACCENT_MAGENTA_SUBTLE_TOKEN = token(
	'color.background.accent.magenta.subtle',
);
const COLOR_BACKGROUND_ACCENT_RED_SUBTLE_TOKEN = token('color.background.accent.red.subtle');
const COLOR_BACKGROUND_ACCENT_ORANGE_SUBTLE_TOKEN = token('color.background.accent.orange.subtle');
const COLOR_BACKGROUND_ACCENT_YELLOW_SUBTLE_TOKEN = token('color.background.accent.yellow.subtle');
const COLOR_BACKGROUND_ACCENT_LIME_SUBTLE_TOKEN = token('color.background.accent.lime.subtle');
const COLOR_BACKGROUND_ACCENT_GREEN_SUBTLE_TOKEN = token('color.background.accent.green.subtle');
const COLOR_BACKGROUND_ACCENT_TEAL_SUBTLE_TOKEN = token('color.background.accent.teal.subtle');
const COLOR_BACKGROUND_ACCENT_GRAY_BOLDER_TOKEN = token('color.background.accent.gray.bolder');
const COLOR_BACKGROUND_ACCENT_BLUE_BOLDER_TOKEN = token('color.background.accent.blue.bolder');
const COLOR_BACKGROUND_ACCENT_PURPLE_BOLDER_TOKEN = token('color.background.accent.purple.bolder');
const COLOR_BACKGROUND_ACCENT_MAGENTA_BOLDER_TOKEN = token(
	'color.background.accent.magenta.bolder',
);
const COLOR_BACKGROUND_ACCENT_RED_BOLDER_TOKEN = token('color.background.accent.red.bolder');
const COLOR_BACKGROUND_ACCENT_ORANGE_BOLDER_TOKEN = token('color.background.accent.orange.bolder');
const COLOR_BACKGROUND_ACCENT_YELLOW_BOLDER_TOKEN = token('color.background.accent.yellow.bolder');
const COLOR_BACKGROUND_ACCENT_LIME_BOLDER_TOKEN = token('color.background.accent.lime.bolder');
const COLOR_BACKGROUND_ACCENT_GREEN_BOLDER_TOKEN = token('color.background.accent.green.bolder');
const COLOR_BACKGROUND_ACCENT_TEAL_BOLDER_TOKEN = token('color.background.accent.teal.bolder');

export const knownBackgroundColors: string[] = [
	ELEVATION_SURFACE_OVERLAY_TOKEN,
	COLOR_BACKGROUND_ACCENT_BLUE_SUBTLEST_TOKEN,
	COLOR_BACKGROUND_ACCENT_PURPLE_SUBTLEST_TOKEN,
	COLOR_BACKGROUND_ACCENT_MAGENTA_SUBTLEST_TOKEN,
	COLOR_BACKGROUND_ACCENT_RED_SUBTLEST_TOKEN,
	COLOR_BACKGROUND_ACCENT_ORANGE_SUBTLEST_TOKEN,
	COLOR_BACKGROUND_ACCENT_YELLOW_SUBTLEST_TOKEN,
	COLOR_BACKGROUND_ACCENT_LIME_SUBTLEST_TOKEN,
	COLOR_BACKGROUND_ACCENT_GREEN_SUBTLEST_TOKEN,
	COLOR_BACKGROUND_ACCENT_TEAL_SUBTLEST_TOKEN,
	COLOR_BACKGROUND_ACCENT_GRAY_SUBTLER_TOKEN,
	COLOR_BACKGROUND_ACCENT_BLUE_SUBTLER_TOKEN,
	COLOR_BACKGROUND_ACCENT_PURPLE_SUBTLER_TOKEN,
	COLOR_BACKGROUND_ACCENT_MAGENTA_SUBTLER_TOKEN,
	COLOR_BACKGROUND_ACCENT_RED_SUBTLER_TOKEN,
	COLOR_BACKGROUND_ACCENT_ORANGE_SUBTLER_TOKEN,
	COLOR_BACKGROUND_ACCENT_YELLOW_SUBTLER_TOKEN,
	COLOR_BACKGROUND_ACCENT_LIME_SUBTLER_TOKEN,
	COLOR_BACKGROUND_ACCENT_GREEN_SUBTLER_TOKEN,
	COLOR_BACKGROUND_ACCENT_TEAL_SUBTLER_TOKEN,
	COLOR_BACKGROUND_ACCENT_GRAY_SUBTLE_TOKEN,
	COLOR_BACKGROUND_ACCENT_BLUE_SUBTLE_TOKEN,
	COLOR_BACKGROUND_ACCENT_PURPLE_SUBTLE_TOKEN,
	COLOR_BACKGROUND_ACCENT_MAGENTA_SUBTLE_TOKEN,
	COLOR_BACKGROUND_ACCENT_RED_SUBTLE_TOKEN,
	COLOR_BACKGROUND_ACCENT_ORANGE_SUBTLE_TOKEN,
	COLOR_BACKGROUND_ACCENT_YELLOW_SUBTLE_TOKEN,
	COLOR_BACKGROUND_ACCENT_LIME_SUBTLE_TOKEN,
	COLOR_BACKGROUND_ACCENT_GREEN_SUBTLE_TOKEN,
	COLOR_BACKGROUND_ACCENT_TEAL_SUBTLE_TOKEN,
	COLOR_BACKGROUND_ACCENT_GRAY_BOLDER_TOKEN,
	COLOR_BACKGROUND_ACCENT_BLUE_BOLDER_TOKEN,
	COLOR_BACKGROUND_ACCENT_PURPLE_BOLDER_TOKEN,
	COLOR_BACKGROUND_ACCENT_MAGENTA_BOLDER_TOKEN,
	COLOR_BACKGROUND_ACCENT_RED_BOLDER_TOKEN,
	COLOR_BACKGROUND_ACCENT_ORANGE_BOLDER_TOKEN,
	COLOR_BACKGROUND_ACCENT_YELLOW_BOLDER_TOKEN,
	COLOR_BACKGROUND_ACCENT_LIME_BOLDER_TOKEN,
	COLOR_BACKGROUND_ACCENT_GREEN_BOLDER_TOKEN,
	COLOR_BACKGROUND_ACCENT_TEAL_BOLDER_TOKEN,
];

export const knownHoveredBackgroundColorsMap: Record<string, string> = {
	[extractToken(ELEVATION_SURFACE_TOKEN)]: token('elevation.surface.hovered'),
	[extractToken(ELEVATION_SURFACE_OVERLAY_TOKEN)]: token('elevation.surface.overlay.hovered'),
	[extractToken(COLOR_BACKGROUND_ACCENT_BLUE_SUBTLEST_TOKEN)]: token(
		'color.background.accent.blue.subtlest.hovered',
	),
	[extractToken(COLOR_BACKGROUND_ACCENT_PURPLE_SUBTLEST_TOKEN)]: token(
		'color.background.accent.purple.subtlest.hovered',
	),
	[extractToken(COLOR_BACKGROUND_ACCENT_MAGENTA_SUBTLEST_TOKEN)]: token(
		'color.background.accent.magenta.subtlest.hovered',
	),
	[extractToken(COLOR_BACKGROUND_ACCENT_RED_SUBTLEST_TOKEN)]: token(
		'color.background.accent.red.subtlest.hovered',
	),
	[extractToken(COLOR_BACKGROUND_ACCENT_ORANGE_SUBTLEST_TOKEN)]: token(
		'color.background.accent.orange.subtlest.hovered',
	),
	[extractToken(COLOR_BACKGROUND_ACCENT_YELLOW_SUBTLEST_TOKEN)]: token(
		'color.background.accent.yellow.subtlest.hovered',
	),
	[extractToken(COLOR_BACKGROUND_ACCENT_LIME_SUBTLEST_TOKEN)]: token(
		'color.background.accent.lime.subtlest.hovered',
	),
	[extractToken(COLOR_BACKGROUND_ACCENT_GREEN_SUBTLEST_TOKEN)]: token(
		'color.background.accent.green.subtlest.hovered',
	),
	[extractToken(COLOR_BACKGROUND_ACCENT_TEAL_SUBTLEST_TOKEN)]: token(
		'color.background.accent.teal.subtlest.hovered',
	),
	[extractToken(COLOR_BACKGROUND_ACCENT_GRAY_SUBTLER_TOKEN)]: token(
		'color.background.accent.gray.subtler.hovered',
	),
	[extractToken(COLOR_BACKGROUND_ACCENT_BLUE_SUBTLER_TOKEN)]: token(
		'color.background.accent.blue.subtler.hovered',
	),
	[extractToken(COLOR_BACKGROUND_ACCENT_PURPLE_SUBTLER_TOKEN)]: token(
		'color.background.accent.purple.subtler.hovered',
	),
	[extractToken(COLOR_BACKGROUND_ACCENT_MAGENTA_SUBTLER_TOKEN)]: token(
		'color.background.accent.magenta.subtler.hovered',
	),
	[extractToken(COLOR_BACKGROUND_ACCENT_RED_SUBTLER_TOKEN)]: token(
		'color.background.accent.red.subtler.hovered',
	),
	[extractToken(COLOR_BACKGROUND_ACCENT_ORANGE_SUBTLER_TOKEN)]: token(
		'color.background.accent.orange.subtler.hovered',
	),
	[extractToken(COLOR_BACKGROUND_ACCENT_YELLOW_SUBTLER_TOKEN)]: token(
		'color.background.accent.yellow.subtler.hovered',
	),
	[extractToken(COLOR_BACKGROUND_ACCENT_LIME_SUBTLER_TOKEN)]: token(
		'color.background.accent.lime.subtler.hovered',
	),
	[extractToken(COLOR_BACKGROUND_ACCENT_GREEN_SUBTLER_TOKEN)]: token(
		'color.background.accent.green.subtler.hovered',
	),
	[extractToken(COLOR_BACKGROUND_ACCENT_TEAL_SUBTLER_TOKEN)]: token(
		'color.background.accent.teal.subtler.hovered',
	),
	[extractToken(COLOR_BACKGROUND_ACCENT_GRAY_SUBTLE_TOKEN)]: token(
		'color.background.accent.gray.subtle.hovered',
	),
	[extractToken(COLOR_BACKGROUND_ACCENT_BLUE_SUBTLE_TOKEN)]: token(
		'color.background.accent.blue.subtle.hovered',
	),
	[extractToken(COLOR_BACKGROUND_ACCENT_PURPLE_SUBTLE_TOKEN)]: token(
		'color.background.accent.purple.subtle.hovered',
	),
	[extractToken(COLOR_BACKGROUND_ACCENT_MAGENTA_SUBTLE_TOKEN)]: token(
		'color.background.accent.magenta.subtle.hovered',
	),
	[extractToken(COLOR_BACKGROUND_ACCENT_RED_SUBTLE_TOKEN)]: token(
		'color.background.accent.red.subtle.hovered',
	),
	[extractToken(COLOR_BACKGROUND_ACCENT_ORANGE_SUBTLE_TOKEN)]: token(
		'color.background.accent.orange.subtle.hovered',
	),
	[extractToken(COLOR_BACKGROUND_ACCENT_YELLOW_SUBTLE_TOKEN)]: token(
		'color.background.accent.yellow.subtle.hovered',
	),
	[extractToken(COLOR_BACKGROUND_ACCENT_LIME_SUBTLE_TOKEN)]: token(
		'color.background.accent.lime.subtle.hovered',
	),
	[extractToken(COLOR_BACKGROUND_ACCENT_GREEN_SUBTLE_TOKEN)]: token(
		'color.background.accent.green.subtle.hovered',
	),
	[extractToken(COLOR_BACKGROUND_ACCENT_TEAL_SUBTLE_TOKEN)]: token(
		'color.background.accent.teal.subtle.hovered',
	),
	[extractToken(COLOR_BACKGROUND_ACCENT_GRAY_BOLDER_TOKEN)]: token(
		'color.background.accent.gray.bolder.hovered',
	),
	[extractToken(COLOR_BACKGROUND_ACCENT_BLUE_BOLDER_TOKEN)]: token(
		'color.background.accent.blue.bolder.hovered',
	),
	[extractToken(COLOR_BACKGROUND_ACCENT_PURPLE_BOLDER_TOKEN)]: token(
		'color.background.accent.purple.bolder.hovered',
	),
	[extractToken(COLOR_BACKGROUND_ACCENT_MAGENTA_BOLDER_TOKEN)]: token(
		'color.background.accent.magenta.bolder.hovered',
	),
	[extractToken(COLOR_BACKGROUND_ACCENT_RED_BOLDER_TOKEN)]: token(
		'color.background.accent.red.bolder.hovered',
	),
	[extractToken(COLOR_BACKGROUND_ACCENT_ORANGE_BOLDER_TOKEN)]: token(
		'color.background.accent.orange.bolder.hovered',
	),
	[extractToken(COLOR_BACKGROUND_ACCENT_YELLOW_BOLDER_TOKEN)]: token(
		'color.background.accent.yellow.bolder.hovered',
	),
	[extractToken(COLOR_BACKGROUND_ACCENT_LIME_BOLDER_TOKEN)]: token(
		'color.background.accent.lime.bolder.hovered',
	),
	[extractToken(COLOR_BACKGROUND_ACCENT_GREEN_BOLDER_TOKEN)]: token(
		'color.background.accent.green.bolder.hovered',
	),
	[extractToken(COLOR_BACKGROUND_ACCENT_TEAL_BOLDER_TOKEN)]: token(
		'color.background.accent.teal.bolder.hovered',
	),
};

export const getBackgroundHoveredColor = (color: string): string => {
	return knownHoveredBackgroundColorsMap[extractToken(color)];
};

// New row of background colors are not semantically correct,
// and will be replaced by actual background colors from ADS
// when available.
// For now, we use these to solve a customer problem by helping
// them select a correctly theme-responsive token value instead
// of having to resort to custom hex codes to get a dark background
// color. Custom hex color codes don't always work well when used alongside
// tokenized text colors because the ADS color tokens will switch
// to a contrasting value in the opposite theme mode, but the custom
// hex background color will stay the same (we only change custom hex
// code values by darkening them if it is a light value selected in light mode).
const additionalBackgroundColors: string[] = [
	token('color.text.accent.gray.bolder'),
	token('color.text.accent.blue.bolder'),
	token('color.text.accent.purple.bolder'),
	token('color.text.accent.magenta.bolder'),
	token('color.text.accent.red.bolder'),
	token('color.text.accent.orange.bolder'),
	token('color.text.accent.yellow.bolder'),
	token('color.text.accent.lime.bolder'),
	token('color.text.accent.green.bolder'),
	token('color.text.accent.teal.bolder'),
];

const knownTextColors: string[] = [
	token('color.text'),
	token('color.text.accent.blue.bolder'),
	token('color.text.accent.purple.bolder'),
	token('color.text.accent.magenta.bolder'),
	token('color.text.accent.red.bolder'),
	token('color.text.accent.orange.bolder'),
	token('color.text.accent.yellow.bolder'),
	token('color.text.accent.lime.bolder'),
	token('color.text.accent.green.bolder'),
	token('color.text.accent.teal.bolder'),
	token('color.text.subtle'),
	token('color.text.accent.blue'),
	token('color.text.accent.purple'),
	token('color.text.accent.magenta'),
	token('color.text.accent.red'),
	token('color.text.accent.orange'),
	token('color.text.accent.yellow'),
	token('color.text.accent.lime'),
	token('color.text.accent.green'),
	token('color.text.accent.teal'),
	token('color.text.inverse'),
	token('color.background.accent.blue.subtler'),
	token('color.background.accent.purple.subtler'),
	token('color.background.accent.magenta.subtler'),
	token('color.background.accent.red.subtler'),
	token('color.background.accent.orange.subtler'),
	token('color.background.accent.yellow.subtler'),
	token('color.background.accent.lime.subtler'),
	token('color.background.accent.green.subtler'),
	token('color.background.accent.teal.subtler'),
];

const knownBorderColors: string[] = [
	token('color.background.accent.gray.subtler'),
	token('color.border.accent.blue'),
	token('color.border.accent.purple'),
	token('color.border.accent.magenta'),
	token('color.border.accent.red'),
	token('color.border.accent.orange'),
	token('color.border.accent.yellow'),
	token('color.border.accent.lime'),
	token('color.border.accent.green'),
	token('color.border.accent.teal'),
	token('color.border.inverse'),
	token('color.border.accent.gray'),
];

const knownHighlightColors: string[] = knownTextColors;

export const useBuiltInColorMap = (
	isSSRProductInDarkMode?: boolean,
): {
	backgroundColorMap: {
		allColors: string[];
		allTokens: string[];
		getResponsiveColor: (color: string) => string;
		humanReadableColors: Map<string, MessageDescriptor>;
		knownColors: string[];
		knownTokens: string[];
	};
	borderColorMap: {
		allColors: string[];
		allTokens: string[];
		getResponsiveColor: (color: string) => string;
		humanReadableColors: Map<string, MessageDescriptor>;
		knownColors: string[];
		knownTokens: string[];
	};
	emptyColorMap: {
		allColors: string[];
		allTokens: string[];
		getResponsiveColor: (color: string) => string;
		humanReadableColors: Map<string, MessageDescriptor>;
		knownColors: string[];
		knownTokens: string[];
	};
	highlightColorMap: {
		allColors: string[];
		allTokens: string[];
		getResponsiveColor: (color: string) => string;
		humanReadableColors: Map<string, MessageDescriptor>;
		knownColors: string[];
		knownTokens: string[];
	};
	textColorMap: {
		allColors: string[];
		allTokens: string[];
		getResponsiveColor: (color: string) => string;
		humanReadableColors: Map<string, MessageDescriptor>;
		knownColors: string[];
		knownTokens: string[];
	};
} => {
	const { getDarkModeCompatibleBackgroundColor } = useDarkModeColors(isSSRProductInDarkMode);

	return useMemo(() => {
		const backgroundColorMap = createBuiltInColorMap({
			knownColors: [...knownBackgroundColors, ...additionalBackgroundColors],
			getResponsiveColor: (color: string) => {
				return getDarkModeCompatibleBackgroundColor(color);
			},
		});

		const borderColorMap = createBuiltInColorMap({
			knownColors: knownBorderColors,
		});
		const textColorMap = createBuiltInColorMap({
			knownColors: knownTextColors,
		});
		const highlightColorMap = createBuiltInColorMap({
			knownColors: knownHighlightColors,
		});

		const emptyColorMap = createBuiltInColorMap({
			knownColors: [],
		});

		return { backgroundColorMap, borderColorMap, textColorMap, highlightColorMap, emptyColorMap };
	}, [getDarkModeCompatibleBackgroundColor]);
};
